import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { getEventById, registerForEvent, updateEventRegistrationPayment } from '../services/event.service';
import { getPaymentSettings, uploadEventPaymentScreenshot } from '../services/payment.service';
import QRCode from "react-qr-code";
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import Header from '../components/HeaderThree';
import ScrollToTop from "react-scroll-to-top";
import Preloader from "../helper/Preloader";
import ColorInit from "../helper/ColorInit";
import NewsletterTwo from "../components/NewsletterTwo";

const EventDetailsPage = () => {
    const { id } = useParams();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paymentSettings, setPaymentSettings] = useState(null);

    const [formData, setFormData] = useState({ name: '', email: '', phone: '', address: '' });
    const [screenshot, setScreenshot] = useState(null);
    const [transactionId, setTransactionId] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);
    const [errors, setErrors] = useState({});
    const [showFullQR, setShowFullQR] = useState(false);
    const [showPaymentDetails, setShowPaymentDetails] = useState(false);
    const [successModalData, setSuccessModalData] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [eventData, pSettings] = await Promise.all([
                    getEventById(id),
                    getPaymentSettings()
                ]);
                setEvent(eventData);
                setPaymentSettings(pSettings);
            } catch (error) {
                console.error("Error fetching event details:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const handleFileChange = (e) => {
        const file = e.target.files[0] || null;
        setScreenshot(file);
        if (file) {
            setErrors(prev => ({ ...prev, screenshot: '' }));
        }
    };

    const handleProceedToPayment = () => {
        setMessage(null);
        const newErrors = {};
        if (!formData.name.trim() || formData.name.trim().length < 2) {
            newErrors.name = 'Please enter a valid name.';
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address.';
        }

        if (!/^\d{10}$/.test(formData.phone)) {
            newErrors.phone = 'Please enter a valid 10-digit phone number.';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        setShowPaymentDetails(true);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setMessage(null);

        const isPaid = event?.price && Number(event.price) > 0;

        // If paid event and payment section not revealed yet, validate candidate details first
        if (isPaid && !showPaymentDetails) {
            handleProceedToPayment();
            return;
        }

        const newErrors = {};
        if (!formData.name.trim() || formData.name.trim().length < 2) {
            newErrors.name = 'Please enter a valid name.';
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address.';
        }

        if (!/^\d{10}$/.test(formData.phone)) {
            newErrors.phone = 'Please enter a valid 10-digit phone number.';
        }

        if (isPaid) {
            if (!transactionId.trim() || transactionId.trim().length !== 12) {
                newErrors.transactionId = 'Please enter a valid 12-digit UTR / Transaction ID.';
            }
            if (!screenshot) {
                newErrors.screenshot = 'Payment screenshot is required for paid workshops.';
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        setSubmitting(true);

        try {
            const registrationData = {
                eventId: event.id,
                eventTitle: event.title,
                name: formData.name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                address: formData.address ? formData.address.trim() : '',
                price: isPaid ? Number(event.price) : 0,
                paymentMethod: isPaid ? "QR_PAYMENT" : "FREE",
                paymentStatus: isPaid ? "AWAITING_VERIFICATION" : "FREE",
                status: "PENDING",
            };

            // 1. Create base registration doc
            const regId = await registerForEvent(registrationData);

            // 2. Upload screenshot and update doc if paid
            if (isPaid && screenshot) {
                const screenshotUrl = await uploadEventPaymentScreenshot(regId, screenshot);
                await updateEventRegistrationPayment(regId, {
                    transactionId: transactionId.trim(),
                    paymentScreenshot: screenshotUrl
                });
            }

            // Trigger Success Modal
            setSuccessModalData({
                name: formData.name.trim(),
                email: formData.email.trim(),
                eventTitle: event.title,
                isPaid,
                price: isPaid ? Number(event.price) : 0,
                transactionId: transactionId.trim()
            });

            setFormData({ name: '', email: '', phone: '', address: '' });
            setTransactionId('');
            setScreenshot(null);
            setShowPaymentDetails(false);
            setMessage(null);
        } catch (error) {
            console.error("Registration error:", error);
            setMessage({ type: 'error', text: 'Failed to complete registration. Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <>
                <ColorInit color={true} />
                <Preloader />
                <Header />
                <div className="text-center py-80">
                    <div className="spinner-border text-main-600" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!event) {
        return (
            <>
                <Header />
                <div className="text-center py-80">
                    <h3>Event not found</h3>
                    <Link to="/workshops" className="btn btn-main-600 mt-24 px-32 rounded-pill">Back to Workshops</Link>
                </div>
                <Footer />
            </>
        );
    }

    const isPaid = event.price && Number(event.price) > 0;
    const eventPrice = isPaid ? Number(event.price) : 0;
    const upiId = paymentSettings?.upiId;
    const merchantName = paymentSettings?.merchantName;
    const upiString = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(merchantName)}&am=${eventPrice.toFixed(2)}&cu=INR`;

    return (
        <>
            <ColorInit color={true} />
            <Preloader />
            <ScrollToTop smooth color="var(--brand-primary)" />
            <Header />
            <Breadcrumb title={event.title} />

            <section className="event-details-section py-80">
                <div className="container">
                    <div className="row gy-5">
                        {/* Column 1: Image */}
                        <div className="col-lg-4">
                            <div className="event-image-sticky sticky-top" style={{ top: '160px', zIndex: 5 }}>
                                <div className="bg-white rounded-24 border border-gray-100 overflow-hidden shadow-sm">
                                    <img
                                        src={event.imageUrl || 'assets/images/thumbs/product-img1.png'}
                                        alt={event.title}
                                        className="w-100 h-400 object-fit-cover"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Column 2: Details */}
                        <div className="col-lg-4">
                            <div className="bg-white rounded-24 border border-gray-100 p-32 shadow-sm">
                                <span className={`badge mb-16 rounded-pill px-16 py-8 text-xs font-bold ${event.type === 'ONLINE' ? 'bg-primary-50 text-primary-600' : 'bg-success-50 text-success-600'}`}>
                                    {event.type || 'WORKSHOP'}
                                </span>
                                <h2 className="mb-16 fw-bold text-2xl">{event.title}</h2>

                                <div className="d-flex items-center gap-16 mb-24 pb-24 border-bottom border-gray-100">
                                    <div className="w-48 h-48 rounded-circle bg-main-50 flex-center text-main-600 text-xl font-bold">
                                        <i className="ph ph-calendar"></i>
                                    </div>
                                    <div>
                                        <span className="text-xs text-gray-500 uppercase font-bold d-block">Date & Time</span>
                                        <span className="text-sm font-bold text-gray-900">{formatDate(event.startAt)}</span>
                                    </div>
                                </div>

                                {event.price !== undefined && (
                                    <div className="d-flex items-center gap-16 mb-24 pb-24 border-bottom border-gray-100">
                                        <div className="w-48 h-48 rounded-circle bg-main-50 flex-center text-main-600 text-xl font-bold">
                                            <i className="ph ph-currency-inr"></i>
                                        </div>
                                        <div>
                                            <span className="text-xs text-gray-500 uppercase font-bold d-block">Registration Fee</span>
                                            <span className="text-lg font-bold text-main-600">
                                                {isPaid ? `Rs.${eventPrice.toFixed(0)}` : 'FREE'}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {event.venue && (
                                    <div className="d-flex items-center gap-16 mb-24 pb-24 border-bottom border-gray-100">
                                        <div className="w-48 h-48 rounded-circle bg-main-50 flex-center text-main-600 text-xl font-bold">
                                            <i className="ph ph-map-pin"></i>
                                        </div>
                                        <div>
                                            <span className="text-xs text-gray-500 uppercase font-bold d-block">Venue</span>
                                            <span className="text-sm font-bold text-gray-900">{event.venue}</span>
                                        </div>
                                    </div>
                                )}

                                {event.description && (
                                    <div className="mt-24">
                                        <h5 className="mb-12 fw-bold text-base">About Workshop</h5>
                                        <p className="text-sm text-gray-600 leading-relaxed">{event.description}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Column 3: Registration & Payment Form */}
                        <div className="col-lg-4">
                            <div className="sticky-top" style={{ top: '160px', zIndex: 5 }}>
                                <div className="bg-white rounded-24 border border-gray-100 p-24 shadow-sm" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                                    <h4 className="mb-8 fw-bold text-lg">Register Now</h4>
                                    <p className="text-xs text-gray-500 mb-20">
                                        {isPaid ? `Registration Fee: Rs.${eventPrice.toFixed(0)}` : 'Free Registration'}
                                    </p>

                                    <form onSubmit={handleRegister} className="row g-12">
                                        <div className="col-12">
                                            <label className="form-label text-[10px] font-bold text-gray-600 uppercase mb-2">Full Name *</label>
                                            <input
                                                type="text"
                                                className={`form-control rounded-pill px-16 py-8 text-sm ${errors.name ? 'border-danger-600' : ''}`}
                                                placeholder="Enter full name"
                                                value={formData.name}
                                                onChange={(e) => {
                                                    setFormData({ ...formData, name: e.target.value });
                                                    if (e.target.value) setErrors(prev => ({ ...prev, name: '' }));
                                                }}
                                                disabled={isPaid && showPaymentDetails}
                                            />
                                            {errors.name && <span className="text-danger-600 text-xs mt-2 d-block">{errors.name}</span>}
                                        </div>

                                        <div className="col-12">
                                            <label className="form-label text-[10px] font-bold text-gray-600 uppercase mb-2">Email Address *</label>
                                            <input
                                                type="email"
                                                className={`form-control rounded-pill px-16 py-8 text-sm ${errors.email ? 'border-danger-600' : ''}`}
                                                placeholder="Enter email address"
                                                value={formData.email}
                                                onChange={(e) => {
                                                    setFormData({ ...formData, email: e.target.value });
                                                    if (e.target.value) setErrors(prev => ({ ...prev, email: '' }));
                                                }}
                                                disabled={isPaid && showPaymentDetails}
                                            />
                                            {errors.email && <span className="text-danger-600 text-xs mt-2 d-block">{errors.email}</span>}
                                        </div>

                                        <div className="col-12">
                                            <label className="form-label text-[10px] font-bold text-gray-600 uppercase mb-2">Phone Number *</label>
                                            <input
                                                type="tel"
                                                className={`form-control rounded-pill px-16 py-8 text-sm ${errors.phone ? 'border-danger-600' : ''}`}
                                                placeholder="10-digit phone number"
                                                maxLength="10"
                                                value={formData.phone}
                                                onChange={(e) => {
                                                    setFormData({ ...formData, phone: e.target.value });
                                                    if (e.target.value.length === 10) setErrors(prev => ({ ...prev, phone: '' }));
                                                }}
                                                disabled={isPaid && showPaymentDetails}
                                            />
                                            {errors.phone && <span className="text-danger-600 text-xs mt-2 d-block">{errors.phone}</span>}
                                        </div>

                                        <div className="col-12">
                                            <label className="form-label text-[10px] font-bold text-gray-600 uppercase mb-2">Address (optional)</label>
                                            <textarea
                                                className="form-control rounded-16 px-16 py-8 text-sm"
                                                placeholder="Address (optional)"
                                                rows="2"
                                                value={formData.address}
                                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                                disabled={isPaid && showPaymentDetails}
                                            ></textarea>
                                        </div>

                                        {/* Button for Paid Event when payment details not yet revealed */}
                                        {isPaid && !showPaymentDetails && (
                                            <div className="col-12 mt-16">
                                                <button
                                                    type="button"
                                                    onClick={handleProceedToPayment}
                                                    className="btn-main-600 w-100 rounded-pill bg-main-600 text-white py-12 shadow-sm fw-bold text-sm transition-2 flex-center gap-8"
                                                >
                                                    <i className="ph ph-credit-card"></i> PROCEED TO PAYMENT (Rs.{eventPrice.toFixed(0)})
                                                </button>
                                            </div>
                                        )}

                                        {/* QR Payment Block Revealed ONLY after candidate details confirmed */}
                                        {isPaid && showPaymentDetails && (
                                            <div className="col-12 mt-16 pt-16 border-top border-gray-200">
                                                <div className="d-flex align-items-center justify-content-between mb-16 bg-main-50 p-12 rounded-12 border border-main-100">
                                                    <div>
                                                        <span className="text-[10px] text-gray-500 uppercase font-bold d-block">Candidate</span>
                                                        <span className="text-xs font-bold text-gray-900">{formData.name} ({formData.phone})</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowPaymentDetails(false)}
                                                        className="btn btn-link text-main-600 text-xs p-0 text-decoration-none font-bold flex-align gap-4"
                                                    >
                                                        <i className="ph ph-pencil-simple"></i> Edit
                                                    </button>
                                                </div>

                                                <h6 className="text-sm fw-bold mb-12">UPI Payment Details</h6>
                                                {paymentSettings ? (
                                                    <div className="text-center mb-24">
                                                        <p className="mb-12 text-sm fw-medium text-gray-700">Scan to pay Rs.{eventPrice.toFixed(0)}</p>
                                                        <div 
                                                            className="position-relative d-inline-block cursor-pointer group bg-white p-16 rounded-12 border border-gray-200 transition-all hover:brightness-90"
                                                            onClick={() => setShowFullQR(true)}
                                                        >
                                                            <div className="position-relative d-inline-block">
                                                                <QRCode 
                                                                    value={upiString} 
                                                                    size={200}
                                                                    level="H"
                                                                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                                                    viewBox={`0 0 256 256`}
                                                                />
                                                                <div className="position-absolute top-50 start-50 translate-middle bg-white p-4 rounded-circle shadow-sm d-flex align-items-center justify-content-center" style={{ width: '56px', height: '56px' }}>
                                                                    <img src="/assets/images/logo/logo2.png" alt="Logo" className="w-100 h-100 object-fit-contain rounded-circle" />
                                                                </div>
                                                            </div>
                                                            <div className="mt-12 pt-12 border-top border-gray-100 text-start ps-8">
                                                                <p className="text-sm fw-bold text-gray-800 mb-8" style={{ wordBreak: 'break-all' }}>
                                                                    <span className="text-xs text-gray-500 uppercase tracking-wider me-8">UPI ID :</span> 
                                                                    {upiId}
                                                                </p>
                                                                <p className="text-sm fw-bold text-gray-800 mb-0">
                                                                    <span className="text-xs text-gray-500 uppercase tracking-wider me-8">UPI NO :</span> 
                                                                    {paymentSettings?.gpayNumber}
                                                                </p>
                                                            </div>
                                                            <div className="position-absolute top-50 start-50 translate-middle bg-main-600 text-white p-8 rounded-circle opacity-0 group-hover-opacity-100 transition-all shadow-lg" style={{ zIndex: 10 }}>
                                                                <i className="ph ph-magnifying-glass-plus fw-bold"></i>
                                                            </div>
                                                            <p className="mt-12 text-xs text-main-600 fw-bold text-center">Click to enlarge</p>
                                                        </div>
                                                        <p className="mt-8 text-sm fw-bold text-gray-800 mb-0">{merchantName}</p>
                                                    </div>
                                                ) : (
                                                    <div className="alert alert-warning text-xs mb-16">Loading payment details...</div>
                                                )}

                                                <div className="space-y-12">
                                                    <div>
                                                        <label className="form-label text-[10px] font-bold text-gray-600 uppercase mb-2">
                                                            Transaction ID / UTR <span className="text-danger-600">*</span>
                                                        </label>
                                                        <input 
                                                            type="text" 
                                                            className={`form-control rounded-pill px-16 py-8 text-sm ${errors.transactionId ? 'border-danger-600' : ''}`} 
                                                            placeholder="Enter 12-digit UTR ID"
                                                            maxLength="12"
                                                            value={transactionId}
                                                            onChange={(e) => {
                                                                setTransactionId(e.target.value);
                                                                if (e.target.value.length === 12) setErrors(prev => ({...prev, transactionId: ''}));
                                                            }}
                                                        />
                                                        {errors.transactionId && <p className="text-danger-600 text-xs mt-2 mb-0">{errors.transactionId}</p>}
                                                    </div>

                                                    <div>
                                                        <label className="form-label text-[10px] font-bold text-gray-600 uppercase mb-2">
                                                            Payment Screenshot <span className="text-danger-600">*</span>
                                                        </label>
                                                        <input 
                                                            type="file" 
                                                            className={`form-control rounded-pill px-16 py-6 text-sm ${errors.screenshot ? 'border-danger-600' : ''}`} 
                                                            accept="image/*"
                                                            onChange={handleFileChange}
                                                        />
                                                        {errors.screenshot && <p className="text-danger-600 text-xs mt-2 mb-0">{errors.screenshot}</p>}
                                                    </div>
                                                </div>

                                                <div className="col-12 mt-16">
                                                    <button
                                                        type="submit"
                                                        className="btn-main-600 w-100 rounded-pill bg-main-600 text-white py-12 shadow-sm fw-bold text-sm transition-2 flex-center gap-8"
                                                        disabled={submitting}
                                                    >
                                                        {submitting ? (
                                                            <><div className="spinner-border spinner-border-sm"></div> Processing...</>
                                                        ) : (
                                                            <><i className="ph ph-check-circle"></i> PAY Rs.{eventPrice.toFixed(0)} & REGISTER</>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Button for Free Event */}
                                        {!isPaid && (
                                            <div className="col-12 mt-16">
                                                <button
                                                    type="submit"
                                                    className="btn-main-600 w-100 rounded-pill bg-main-600 text-white py-12 shadow-sm fw-bold text-sm transition-2 flex-center gap-8"
                                                    disabled={submitting}
                                                >
                                                    {submitting ? (
                                                        <><div className="spinner-border spinner-border-sm"></div> Processing...</>
                                                    ) : (
                                                        <><i className="ph ph-check-circle"></i> BOOK NOW (FREE)</>
                                                    )}
                                                </button>
                                            </div>
                                        )}

                                        {message && (
                                            <div className="col-12 mt-12">
                                                <div className={`p-12 rounded-12 text-center text-xs fw-bold ${message.type === 'success' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
                                                    {message.text}
                                                </div>
                                            </div>
                                        )}

                                        <div className="col-12">
                                            <p className="text-[9px] text-gray-400 text-center mb-0 mt-8">By registering, you agree to our terms.</p>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Enlarged QR Modal Portal */}
            {showFullQR && createPortal(
                <div 
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center transition-all"
                    style={{ backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 999999 }}
                    onClick={() => setShowFullQR(false)}
                >
                    <div className="position-relative p-16 animate-zoomIn d-flex flex-column align-items-center" style={{ zIndex: 1000000, maxWidth: '100%' }} onClick={e => e.stopPropagation()}>
                        <button 
                            className="position-absolute top-0 end-0 m-n16 bg-white w-40 h-40 rounded-circle border-0 shadow-lg flex-center"
                            onClick={() => setShowFullQR(false)}
                            style={{ zIndex: 1000001, cursor: 'pointer' }}
                        >
                            <i className="ph ph-x fw-bold text-xl"></i>
                        </button>
                        <div className="rounded-24 shadow-2xl bg-white p-24 d-inline-block">
                            <div className="position-relative d-inline-block">
                                <QRCode 
                                    value={upiString} 
                                    size={300}
                                    level="H"
                                    style={{ height: "auto", maxWidth: "90vw", maxHeight: "80vh", display: 'block' }}
                                    viewBox={`0 0 256 256`}
                                />
                                <div className="position-absolute top-50 start-50 translate-middle bg-white p-8 rounded-circle shadow-sm d-flex align-items-center justify-content-center" style={{ width: '84px', height: '84px' }}>
                                    <img src="/assets/images/logo/logo2.png" alt="Logo" className="w-100 h-100 object-fit-contain rounded-circle" />
                                </div>
                            </div>
                            <div className="mt-16 pt-16 border-top border-gray-100 text-start ps-12">
                                <p className="text-lg fw-bold text-gray-800 mb-8" style={{ wordBreak: 'break-all' }}>
                                    <span className="text-sm text-gray-500 uppercase tracking-wider me-12">UPI ID :</span> 
                                    {upiId}
                                </p>
                                <p className="text-lg fw-bold text-gray-800 mb-0">
                                    <span className="text-sm text-gray-500 uppercase tracking-wider me-12">UPI NO :</span> 
                                    {paymentSettings?.gpayNumber}
                                </p>
                            </div>
                        </div>
                        <div className="mt-24 text-center text-white">
                            <h5 className="mb-8 text-white fw-bold">Scan to Pay Rs.{eventPrice.toFixed(0)}</h5>
                            <p className="text-sm fw-medium mb-12">{merchantName}</p>
                            <p className="mb-0 opacity-50 text-xs">Tap anywhere outside to close</p>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Registration Success Confirmation Modal */}
            {successModalData && createPortal(
                <div 
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center transition-all"
                    style={{ backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 999999 }}
                    onClick={() => setSuccessModalData(null)}
                >
                    <div 
                        className="position-relative bg-white rounded-24 p-32 text-center animate-zoomIn shadow-2xl overflow-y-auto" 
                        style={{ maxWidth: '480px', width: '90%', maxHeight: '90vh', zIndex: 1000000 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <button 
                            type="button" 
                            onClick={() => setSuccessModalData(null)}
                            className="position-absolute top-0 end-0 m-16 bg-gray-100 text-gray-500 hover-text-gray-900 w-32 h-32 rounded-circle border-0 flex-center text-sm font-bold cursor-pointer"
                            style={{ zIndex: 10 }}
                        >
                            ✕
                        </button>

                        <div className="w-64 h-64 bg-green-100 text-green-600 rounded-circle flex-center mx-auto mb-20 shadow-sm" style={{ width: '72px', height: '72px', background: '#dcfce7', color: '#16a34a' }}>
                            <i className="ph ph-check-circle fw-bold text-4xl"></i>
                        </div>
                        
                        <h4 className="fw-bold text-gray-900 mb-8">Registration Submitted!</h4>
                        <p className="text-sm text-gray-600 mb-20">
                            Thank you <strong>{successModalData.name}</strong>! Your registration for <strong>{successModalData.eventTitle}</strong> has been successfully placed.
                        </p>

                        <div className="bg-gray-50 rounded-16 p-16 border border-gray-100 text-start text-xs space-y-8 mb-24">
                            <div className="flex-between mb-8">
                                <span className="text-gray-500">Candidate Email:</span>
                                <span className="fw-bold text-gray-800">{successModalData.email}</span>
                            </div>
                            <div className="flex-between mb-8">
                                <span className="text-gray-500">Registration Fee:</span>
                                <span className="fw-bold text-main-600">{successModalData.isPaid ? `Rs.${successModalData.price}` : 'FREE'}</span>
                            </div>
                            {successModalData.transactionId && (
                                <div className="flex-between">
                                    <span className="text-gray-500">Transaction UTR:</span>
                                    <span className="fw-bold text-gray-800">{successModalData.transactionId}</span>
                                </div>
                            )}
                        </div>

                        <p className="text-xs text-gray-500 mb-24">
                            A confirmation email has been sent to your email address. Our team will verify your details shortly.
                        </p>

                        <div className="row g-12">
                            <div className="col-6">
                                <Link 
                                    to="/workshops" 
                                    className="btn bg-main-600 text-white w-100 rounded-pill py-12 text-xs fw-bold shadow-sm d-flex align-items-center justify-content-center text-decoration-none"
                                    onClick={() => setSuccessModalData(null)}
                                >
                                    Explore Workshops
                                </Link>
                            </div>
                            <div className="col-6">
                                <button 
                                    type="button" 
                                    className="btn bg-gray-100 text-gray-700 hover-bg-gray-200 border border-gray-200 w-100 rounded-pill py-12 text-xs fw-bold d-flex align-items-center justify-content-center"
                                    onClick={() => setSuccessModalData(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* NewsletterTwo */}
            <NewsletterTwo />
            <Footer />
        </>
    );
};

export default EventDetailsPage;